import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, mergeMap, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppInsightsFeatureType } from '../../../core/models/app-insights-feature-type';
import { selectUrl } from '../../../core/router/store/selectors/router.selector';
import {
  resolvePatientSwitchRoute,
  stripEntityScopedQueryParams,
} from '../../../shared/functions/patient-switch-route.functions';
import { AppInsightsService } from '../../../core/services/app-insights/app-insights.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ConfigService } from '../../../core/services/config/config.service';
import { IdleTimeoutService } from '../../../core/services/idle-timeout/idle-timeout.service';
import { setGlobalError } from '../../../core/store/actions/app.actions';
import { selectNavList } from '../../../core/store/selectors/app.selector';
import { NavListItem } from '../../../shared/components/layout/models/nav-list-item';
import { UserStatusType } from '../../data/user-status-type';
import { getWorkflowBlockingRoute, WORKFLOW_HOLDING_ROUTE, WORKFLOW_REVIEW_ROUTE } from '../../data/workflow-route';
import { UserPatient } from '../../models/user';
import { UserRequestService } from '../../services/user-request/user-request.service';
import { UsersService } from '../../services/users/users.service';
import {
  getUser,
  getUserFailure,
  getUserSuccess,
  handleInvalidPatientIdNavigation,
  insertUserRequest,
  insertUserRequestFailure,
  insertUserRequestSuccess,
  patientSwitchInitiated,
  setPatientId,
  setPatientIdSuccess,
} from '../actions/user.actions';
import {
  selectPatientId,
  selectUserRequestWorkflowStatusTypeId,
  selectUserStatusTypeId,
  selectValidPatientIds,
} from '../selectors/user.selectors';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private usersService = inject(UsersService);
  private userRequestService = inject(UserRequestService);
  private authService = inject(AuthService);
  private configService = inject(ConfigService);
  private idleTimeoutService = inject(IdleTimeoutService);
  private appInsightsService = inject(AppInsightsService);

  setPatientId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setPatientId),
      concatLatestFrom(() => [this.store.select(selectValidPatientIds)]),
      concatMap(([action, validPatientIds]) => {
        if (!validPatientIds.includes(action.patientId)) {
          this.appInsightsService.trackEvent('User.PatientChanged.Failure', { feature: AppInsightsFeatureType.USER });
          return of(handleInvalidPatientIdNavigation());
        }

        // A page load is not a patient change: AppComponent replays the patientId already in
        // window.location.pathname once the user finishes loading, so this arrives on every reload
        // of /patient/:patientId/<page>. Running the switch pipeline over it is actively harmful —
        // patientSwitchInitiated cancels the requests the loading page has just started, and the
        // reset meta-reducer wipes every patient slice underneath it (bug 57454).
        //
        // setPatientIdSuccess is still emitted, because it is a *load* trigger and not just a
        // routing signal: it is one of three inputs to ProfileEffects.getProfile$, whose
        // getProfileSuccess is the profile slice's only writer. Swallowing it left any refreshed
        // page that never mounts app-profile-widget (e.g. a goal detail's print header) with no
        // profile at all — and only for the account's *default* patient, since getUser$ seeds that
        // id while any other patient still took the switch path.
        if (action.trigger === 'page-load') {
          return of(setPatientIdSuccess({ patientId: action.patientId, trigger: 'page-load' }));
        }

        this.appInsightsService.trackEvent('User.PatientChanged.Success', { feature: AppInsightsFeatureType.USER });
        return of(patientSwitchInitiated(), setPatientIdSuccess({ patientId: action.patientId, trigger: 'switch' }));
      })
    );
  });

  setPatientIdSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setPatientIdSuccess),
        tap(action => {
          // Nothing to re-route on a page load: the URL the browser asked for *is* the destination.
          // The replay commits while that first navigation is still parked in authGuard (which
          // waits on isAuthComplete, then round-trips /Authorize), so router.url is still the
          // uncommitted '/' — it matched no patient route and sent the patient to Home instead of
          // the page they reloaded (bug 57454).
          //
          // The trigger is read off the action rather than sniffed from router.currentNavigation()
          // so this behaves identically everywhere: under local/e2e auth bypass the guard resolves
          // in a microtask, the navigation is settled by the time the replay lands, and a
          // timing-based check would fall through to the switch path in the only environment a
          // developer can actually exercise. Remap rules are skipped deliberately: they exist
          // because entity ids belong to the *previous* patient, and on a page load the URL is
          // already this patient's own.
          if (action.trigger === 'page-load') {
            this.appInsightsService.trackEvent('User.PatientChanged.RouteOutcome', {
              feature: AppInsightsFeatureType.USER,
              outcome: 'page-load',
            });
            return;
          }

          const { outcome, commands } = resolvePatientSwitchRoute(this.router.url, action.patientId);

          this.appInsightsService.trackEvent('User.PatientChanged.RouteOutcome', {
            feature: AppInsightsFeatureType.USER,
            outcome,
          });

          if (outcome === 'preserve') {
            // Strip entity-scoped params (letterId, goalId) so the previous patient's entity id
            // doesn't carry onto the new patient's URL.
            const queryParams = stripEntityScopedQueryParams(this.router.parseUrl(this.router.url).queryParams);
            if (Object.keys(queryParams).length > 0) {
              this.router.navigate(commands, { queryParams });
            } else {
              this.router.navigate(commands);
            }
          } else {
            this.router.navigate(commands);
          }
        })
      );
    },
    { dispatch: false }
  );

  handleInvalidPatientIdNavigation$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(handleInvalidPatientIdNavigation),
        concatLatestFrom(() => [
          this.store.select(selectUserStatusTypeId),
          this.store.select(selectUserRequestWorkflowStatusTypeId),
        ]),
        tap(([, userStatusTypeId, userRequestWorkflowStatusTypeId]) => {
          const workflowRoute = getWorkflowBlockingRoute(userRequestWorkflowStatusTypeId);
          if (workflowRoute) {
            this.router.navigate([workflowRoute]);
          } else if (userStatusTypeId !== UserStatusType.Active) {
            this.router.navigate(['assign-patient']);
          } else {
            this.router.navigate(['invalid-patient']);
          }
        })
      );
    },
    { dispatch: false }
  );

  getUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUser),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([action, patientId]) => {
        const apiCall = action.isSilentLogin
          ? this.usersService.getUserByAzureId(action.user.azureId)
          : this.usersService.getOrInsertUser(action.user);
        return apiCall.pipe(
          map(user => {
            this.appInsightsService.setAuthenticatedUser(user.azureId);
            this.authService.isAuthComplete.set(true);

            const workflowRoute = getWorkflowBlockingRoute(user.userRequestWorkflowStatusTypeId);
            if (workflowRoute || user.userStatusTypeId !== UserStatusType.Active) {
              patientId = 0;
            } else if (patientId !== null && patientId !== 0) {
              const validPatient = user.userPatients.find((up: UserPatient) => up.patientId === patientId);
              if (!validPatient) {
                patientId = -1;
              }
            } else {
              const defaultPatient = user.userPatients.find((up: UserPatient) => up.isDefault);
              // A user with no default patient is an unexpected state; surface it as a failure
              // rather than silently succeeding with an ambiguous patientId (preserves the prior
              // throw-into-catchError behavior without a non-null assertion).
              if (!defaultPatient) {
                return getUserFailure({ error: undefined });
              }
              patientId = defaultPatient.patientId;
            }

            return getUserSuccess({ user, patientId });
          }),
          catchError(err => {
            console.error(
              '[getUser] API error. In local bypass mode, your token may be expired — update config.local.json.',
              err
            );
            this.authService.isAuthComplete.set(true);
            if (environment.allowAuthBypass && this.configService.config?.localAuthBypass) {
              const status = err?.status ?? err?.error?.status;
              const hint =
                status === 401
                  ? 'Your localAuthToken is expired. Grab a fresh token from DevTools and update config.local.json.'
                  : 'API call failed in local bypass mode. Check the console for details.';
              return of(setGlobalError({ error: hint }), getUserFailure({ error: err.error?.errors }));
            }
            return of(getUserFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  navigateAfterAuthorization$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getUserSuccess, insertUserRequestSuccess),
        concatLatestFrom(() => [
          this.store.select(selectUserStatusTypeId),
          this.store.select(selectUrl),
          this.store.select(selectPatientId),
          this.store.select(selectNavList),
        ]),
        tap(([action, userStatusTypeId, url, patientId, navList]) => {
          this.idleTimeoutService.start();
          const currentPath = (url ?? window.location.pathname.replace(/^\//, '')).split(/[?#]/)[0];
          const workflowRoute = getWorkflowBlockingRoute(action.user.userRequestWorkflowStatusTypeId);
          if (workflowRoute) {
            if (currentPath !== workflowRoute) {
              this.router.navigate([workflowRoute]);
            }
            return;
          }

          if (patientId === -1) {
            this.router.navigate(['invalid-patient']);
          } else if (userStatusTypeId !== UserStatusType.Active) {
            const isGuardedRoute = navList?.find((f: NavListItem) => f.route === url)?.isGuardedRoute;
            if (isGuardedRoute === undefined || isGuardedRoute) {
              const route = userStatusTypeId === UserStatusType.Pending ? 'assign-patient' : 'unauthorized';
              this.router.navigate([route]);
            } else {
              this.router.navigate([url]);
            }
          } else if (
            url === '' ||
            url?.startsWith('#') ||
            url === 'assign-patient' ||
            url === 'error' ||
            // A user who lands back on a (now-unblocked) workflow route — e.g. after the holding
            // screen auto-refreshes once setup completes — should be taken home, not left stranded.
            // Uses currentPath (not url) so a reload where the router store hasn't committed yet, or a
            // route carrying a query/fragment, still bounces home instead of stranding the user.
            currentPath === WORKFLOW_HOLDING_ROUTE ||
            currentPath === WORKFLOW_REVIEW_ROUTE
          ) {
            this.router.navigate(['patient', patientId]);
          }
        })
      );
    },
    { dispatch: false }
  );

  insertUserRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(insertUserRequest),
      mergeMap(action => {
        return this.userRequestService.insertUserRequest(action.request).pipe(
          map(user => {
            this.appInsightsService.trackEvent('User.Activation.Success');
            this.authService.isAuthComplete.set(true);
            const patientId = 0; // New users won't have a patient assigned
            return insertUserRequestSuccess({ user, patientId });
          }),
          catchError(err => {
            this.appInsightsService.trackEvent('User.Activation.Failure');
            this.authService.isAuthComplete.set(false);
            return of(
              insertUserRequestFailure({
                error:
                  err.error?.errors ??
                  "We couldn't find a matching patient record. Please try again or contact support at (844) 474-6522.",
              })
            );
          })
        );
      })
    );
  });
}
